import { FilterSearchIcon } from '@/assets/icons/filterSearch';
import { AiAssistantIcon } from '@/assets/static/icons/ai-assistant';
import Label from '@/components/basicComponents/label';
import { Loader } from '@/components/loaders/loader';
import { PasswordToggleButton } from '@/components/passwordToggleButton';
import { themeVar } from '@/styles/theme';
import { useState } from 'react';
import styled, { css } from 'styled-components';

const getInputSize = (
  size: InputProps['size'],
  styleType: InputProps['styleType'],
  withDollarSign: boolean,
  withPasswordIcon: boolean,
  withSearchIcon: boolean
) => {
  let paddingRight = '0px';
  let paddingLeft = '0px';

  if (withPasswordIcon) {
    paddingRight = '30px';
  }

  if (withSearchIcon) {
    paddingRight = '45px';
  }

  if (withDollarSign) {
    paddingLeft = '12px';
  }

  if (styleType === 'magicWand') {
    paddingLeft = '50px';
  }

  switch (size) {
    case 'sm':
      return css`
        padding: 8px ${`calc(5px + ${paddingRight})`} 8px ${paddingLeft ? `calc(5px + ${paddingLeft})` : '5px'};
        border-radius: 3px;
      `;
    case 'md':
      return css`
        padding: 10px ${`calc(10px + ${paddingRight})`} 10px ${`calc(10px + ${paddingLeft})`};
        border-radius: 4px;
      `;
    case 'lg':
      return css`
        padding: 10px ${`calc(12px + ${paddingRight})`} 10px ${`calc(12px + ${paddingLeft})`};
        border-radius: 5px;
      `;
    default:
      return '';
  }
};

const getInputStyleType = (styleType: InputProps['styleType']) => {
  switch (styleType) {
    case 'default':
      return css`
        background-color: ${themeVar('layer.1')};
        border: 1px solid ${themeVar('border.base')};
        color: ${themeVar('font.strong')};

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px ${themeVar('layer.1')} inset;
          -webkit-text-fill-color: ${themeVar('font.strong')};
        }
      `;
    case 'form':
      return css`
        background-color: ${themeVar('layer.2')};
        border: 1px solid ${themeVar('border.base')};
        color: ${themeVar('font.strong')};

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px ${themeVar('layer.2')} inset;
          -webkit-text-fill-color: ${themeVar('font.strong')};
        }
      `;
    case 'success':
      return css`
        background-color: #efffea;
        border: 1px solid ${themeVar('context.success')};
        color: #2e2e2e;

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px #efffea inset;
          -webkit-text-fill-color: #2e2e2e;
        }
      `;
    case 'error':
      return css`
        background-color: #ffeaec;
        border: 1px solid ${themeVar('context.error')};
        color: #2e2e2e;

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px #ffeaec inset;
          -webkit-text-fill-color: #2e2e2e;
        }
      `;
    case 'disabled':
      return css`
        background-color: ${themeVar('action.disabled')};
        border: 1px solid ${themeVar('border.base')};
        color: ${themeVar('font.disabled')};
      `;
    case 'magicWand':
      return css`
        background-color: ${themeVar('action.disabled')};
        border: 1px solid ${themeVar('border.base')};
        color: ${themeVar('font.base')};
      `;
    default:
      return '';
  }
};

const getPasswordIconTopPosition = (size: InputProps['size']) => {
  switch (size) {
    case 'sm':
      return css`
        top: 6px;
      `;
    case 'md':
      return css`
        top: 10px;
      `;
    case 'lg':
      return css`
        top: 12px;
      `;
    default:
      return '';
  }
};

const getDollarSignTopPosition = (size: InputProps['size']) => {
  switch (size) {
    case 'sm':
      return css`
        top: 9px;
      `;
    case 'md':
      return css`
        top: 11px;
      `;
    case 'lg':
      return css`
        top: 11px;
      `;
    default:
      return '';
  }
};

const getFontProperties = (size: InputProps['size']) => {
  switch (size) {
    case 'sm':
      return css`
        font-size: 13px;
        line-height: 18.2px;
      `;
    case 'md':
      return css`
        font-size: 16px;
        line-height: 22.4px;
      `;
    case 'lg':
      return css`
        font-size: 19px;
        line-height: 26.6px;
      `;
    default:
      return '';
  }
};

const magicWandIconTopPositions = {
  sm: css`
    top: 4px;
  `,
  md: css`
    top: 8px;
  `,
  lg: css`
    top: 11px;
  `
};

const searchIconSizes = {
  sm: css`
    width: 36.2px;
    height: 36.2px;
    border-radius: 0 3px 3px 0;
  `,
  md: css`
    width: 44.4px;
    height: 44.4px;
    border-radius: 0 4px 4px 0;
  `,
  lg: css`
    width: 48.6px;
    height: 48.6px;
    border-radius: 0 5px 5px 0;
  `
};

interface InputProps {
  value?: string | number;
  onChange?: (event?: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event?: React.MouseEvent<HTMLInputElement>) => void;
  onKeyUp?: (event?: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (event?: React.FocusEvent<HTMLInputElement>) => void;
  ref?: React.LegacyRef<HTMLInputElement>;
  size?: 'sm' | 'md' | 'lg';
  styleType?: 'default' | 'form' | 'success' | 'error' | 'disabled' | 'magicWand';
  type?: 'text' | 'number' | 'email' | 'password';
  name?: string;
  width?: string;
  description?: string;
  errorText?: string;
  label?: string;
  tooltip?: string;
  tooltipWidth?: number;
  isRequiredField?: boolean;
  withPasswordIcon?: boolean;
  withSearchIcon?: boolean;
  withDollarSign?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  autoComplete?: string;
  className?: string;
  loading?: boolean;
  'data-testid'?: string;
  'data-testid-label'?: string;
  'data-testid-error'?: string;
}

const Input = ({
  value,
  onChange,
  onClick,
  onKeyUp,
  onBlur,
  ref,
  size = 'lg',
  styleType = 'default',
  type,
  name,
  width = '100%',
  description,
  errorText,
  label,
  tooltip,
  tooltipWidth = 0,
  isRequiredField = false,
  withPasswordIcon = false,
  withSearchIcon = false,
  withDollarSign = false,
  placeholder = '',
  autoFocus = false,
  autoComplete,
  className,
  loading = false,
  'data-testid': testId,
  'data-testid-label': labelTestId,
  'data-testid-error': errorTestId
}: InputProps) => {
  const [isPasswordMasked, setIsPasswordMasked] = useState(withPasswordIcon);

  return (
    <InputWrapper>
      {label && (
        <Label required={isRequiredField} size={size} tooltip={tooltip} tooltipWidth={tooltipWidth} data-testid={labelTestId}>
          {label}
        </Label>
      )}
      <StyledInputWrapper>
        <StyledInput
          data-testid={testId}
          type={withPasswordIcon ? (isPasswordMasked ? type : 'text') : type}
          value={value}
          onChange={onChange}
          onClick={onClick}
          onKeyUp={onKeyUp}
          onBlur={onBlur}
          ref={ref}
          disabled={styleType === 'disabled' || styleType === 'magicWand' || loading}
          placeholder={placeholder}
          name={name}
          width={width}
          size={size}
          styleType={styleType}
          withPasswordIcon={withPasswordIcon}
          withSearchIcon={withSearchIcon}
          withDollarSign={withDollarSign}
          className={className}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
        />
        {styleType === 'magicWand' && (
          <MagicWandIconWrapper size={size}>
            <AiAssistantIcon width={28} height={28} fill={themeVar('font.weak')} />
          </MagicWandIconWrapper>
        )}
        {withDollarSign && (
          <Dollar size={size} styleType={styleType}>
            $
          </Dollar>
        )}
        {withPasswordIcon && (
          <PasswordIcon size={size}>
            <PasswordToggleButton
              isPasswordMasked={isPasswordMasked}
              setIsPasswordMasked={setIsPasswordMasked}
              data-testid={testId ? `${testId}.password-toggle` : undefined}
            />
          </PasswordIcon>
        )}
        {withSearchIcon && (
          <SearchIconWrapper size={size}>
            <FilterSearchIcon fill={themeVar('font.action')} />
          </SearchIconWrapper>
        )}
        {loading && (
          <LoaderWrapper>
            <Loader size={20} />
          </LoaderWrapper>
        )}
      </StyledInputWrapper>
      {description && <Description>{description}</Description>}
      {errorText && <ErrorText size={size} data-testid={errorTestId}>{errorText}</ErrorText>}
    </InputWrapper>
  );
};

export default Input;

const InputWrapper = styled.div`
  width: 100%;
`;

const StyledInputWrapper = styled.div`
  position: relative;
`;

const StyledInput = styled.input<InputProps>`
  font-family: Blinker;
  font-style: normal;
  font-weight: 400;
  width: ${({ width }) => width};
  cursor: ${({ styleType }) => (styleType === 'disabled' ? 'not-allowed' : 'auto')};

  ${({ size, styleType, withDollarSign, withPasswordIcon, withSearchIcon }) =>
    getInputSize(size, styleType, withDollarSign, withPasswordIcon, withSearchIcon)}
  ${({ styleType }) => getInputStyleType(styleType)}
  ${({ size }) => getFontProperties(size)}

  &:focus {
    outline: none;
  }

  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const Dollar = styled.span<{ size: InputProps['size']; styleType: InputProps['styleType'] }>`
  position: absolute;
  left: 10px;
  color: ${({ styleType }) =>
    styleType === 'success' || styleType === 'error' ? '#2e2e2e' : styleType === 'disabled' ? themeVar('font.disabled') : themeVar('font.strong')};

  ${({ size }) => getFontProperties(size)}
  ${({ size }) => getDollarSignTopPosition(size)}
`;

const MagicWandIconWrapper = styled.div<{ size: InputProps['size'] }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 10px;
  padding-right: 10px;
  border-right: 1px solid ${themeVar('border.base')};

  ${({ size }) => magicWandIconTopPositions[size]}
`;

const PasswordIcon = styled.div<{ size: InputProps['size'] }>`
  position: absolute;
  right: 12px;

  ${({ size }) => getPasswordIconTopPosition(size)}
`;

const SearchIconWrapper = styled.div<{ size: InputProps['size'] }>`
  position: absolute;
  top: 0px;
  right: 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  background: ${themeVar('layer.base')};
  border: 1px solid ${themeVar('font.action')};

  ${({ size }) => searchIconSizes[size]}
`;

const Description = styled.span`
  font-weight: 300;
  font-size: 13px;
  line-height: 18.2px;
  color: ${themeVar('font.weak')};
`;

export const ErrorText = styled.p<{ size: InputProps['size'] }>`
  color: ${themeVar('context.error')};
  font-family: Blinker;
  font-style: normal;
  font-weight: 400;

  ${({ size }) => getFontProperties(size)}
`;

const LoaderWrapper = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
`;
